import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { createWorkout } from '../api';
import { useAuth } from '../contexts/AuthContext';
import { ArrowLeft, Info } from 'lucide-react';

const WorkoutForm: React.FC = () => {
  const { planId } = useParams<{ planId: string }>();
  const [type, setType] = useState('');
  const [date, setDate] = useState('');
  const [duration, setDuration] = useState('');
  const [distance, setDistance] = useState('');
  const [notes, setNotes] = useState('');
  const [completed, setCompleted] = useState(false);
  const { user } = useAuth();
  const navigate = useNavigate();

  const validateRules = () => {
    if (type === 'run') {
      alert('Remember: Avoid Key Run on same day as Bike unless Brick.');
    }
    return true;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validateRules()) return;
    if (user && planId) {
      try {
        await createWorkout(user, {
          training_plan_id: planId,
          type,
          date,
          duration_minutes: parseInt(duration),
          distance_km: parseFloat(distance),
          notes,
          completed,
        });
        navigate(`/workouts/${planId}`);
      } catch (error) {
        console.error('Error creating workout:', error);
      }
    }
  };

  return (
    <div>
      <button
        onClick={() => navigate(`/workouts/${planId}`)}
        className="flex items-center space-x-2 text-muted hover:text-[#3b82f6] mb-6 transition-colors"
      >
        <ArrowLeft size={18} />
        <span>Back to Workouts</span>
      </button>

      <div className="max-w-2xl">
        <h1 className="text-3xl font-bold mb-2">Log New Workout</h1>
        <p className="text-muted mb-8">Record your training session details.</p>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div>
            <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Workout Type</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="w-full"
              required
            >
              <option value="">Select Type</option>
              <option value="swim">🏊 Swim</option>
              <option value="bike">🚴 Bike</option>
              <option value="run">🏃 Run</option>
              <option value="strength">💪 Strength</option>
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Date</label>
              <input
                type="date"
                value={date}
                onChange={(e) => setDate(e.target.value)}
                className="w-full"
                required
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Duration (minutes)</label>
              <input
                type="number"
                placeholder="e.g., 60"
                value={duration}
                onChange={(e) => setDuration(e.target.value)}
                className="w-full"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Distance (km)</label>
            <input
              type="number"
              step="0.1"
              placeholder="e.g., 10.5"
              value={distance}
              onChange={(e) => setDistance(e.target.value)}
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-[#e2e8f0] mb-2">Notes</label>
            <textarea
              placeholder="How did the session feel? Any observations..."
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              className="w-full h-32 resize-none"
            />
          </div>

          <div className="flex items-center space-x-3">
            <input
              type="checkbox"
              id="completed"
              checked={completed}
              onChange={(e) => setCompleted(e.target.checked)}
              className="w-5 h-5 rounded bg-[#141921] border-[#2d3748]"
            />
            <label htmlFor="completed" className="text-[#e2e8f0]">Mark as completed</label>
          </div>

          <div className="bg-[rgba(59,130,246,0.1)] border border-[rgba(59,130,246,0.2)] rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Info className="text-[#3b82f6] mt-0.5" size={18} />
              <div className="text-sm">
                <p className="font-medium text-[#e2e8f0]">Training Tip</p>
                <p className="text-muted mt-1">Always have a recovery day each week. If doing swim + another session, complete swim first.</p>
              </div>
            </div>
          </div>

          <div className="flex items-center space-x-4 pt-4">
            <button
              type="submit"
              className="px-6 py-2 bg-[#3b82f6] text-white rounded-lg hover:bg-[#60a5fa] transition-colors"
            >
              Log Workout
            </button>
            <button
              type="button"
              onClick={() => navigate(`/workouts/${planId}`)}
              className="px-6 py-2 text-[#e2e8f0] hover:text-[#ffffff] transition-colors"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default WorkoutForm;
